import { useAnalytics } from '@/hooks/use-analytics';
import { useProtectedImage } from '@/hooks/use-protected-image';
import { useTouchDevice } from '@/hooks/use-touch-device';
import { cn } from '@/lib/utils';
import type { GalleryPhoto } from '@/types';
import { useCallback, useEffect, useState } from 'react';
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

interface ProfileImagesProps {
  images?: GalleryPhoto[];
  /** Tailwind aspect-ratio class, e.g. "aspect-[4/5]" or "aspect-square". Defaults to aspect-[4/5]. */
  aspectClass?: string;
  /** If true, the first image will be eager loaded. Defaults to false. */
  priority?: boolean;
  /** Profile ID used for analytics tracking. */
  profileId?: string | number | null;
}

/** Wraps a carousel arrow so its click doesn't bubble to the card <a>. */
function ArrowWrapper({ children }: { children: React.ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span
      className="contents"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {children}
    </span>
  );
}

/**
 * Single photo slide — isolated into its own component so each photo
 * gets its own useProtectedImage hook instance.
 */
function PhotoSlide({
  image,
  index,
  aspectClass,
  priority,
  active,
}: {
  image: GalleryPhoto;
  index: number;
  aspectClass: string;
  priority: boolean;
  active: boolean;
}) {
  const thumbUrl = image.thumb || image.url;
  const { src } = useProtectedImage(active ? thumbUrl : null);
  const [loaded, setLoaded] = useState(false);

  // Reset fade-in when a new src is assigned (e.g. swipe reveals a new slide).
  useEffect(() => {
    if (src) setLoaded(false);
  }, [src]);

  return (
    <div className={cn('relative overflow-hidden bg-black', aspectClass)}>
      {/* Blurred background — purely decorative */}
      <img
        src={src ?? undefined}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-lg brightness-75"
        style={{ objectPosition: `${image.focal_x}% ${image.focal_y}%` }}
        draggable={false}
      />
      {/* Sharp foreground — fades in once loaded to avoid black→image pop. */}
      <img
        src={src ?? undefined}
        alt={`Photo ${index + 1}`}
        className={cn(
          'relative h-full w-full object-cover select-none transition-opacity duration-200 ease-in',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        style={{
          objectPosition: `${image.focal_x}% ${image.focal_y}%`,
          WebkitTouchCallout: 'none',
        }}
        draggable={false}
        onLoad={() => setLoaded(true)}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}

export function ProfileImages({
  images = [],
  aspectClass = 'aspect-[4/5]',
  priority = false,
  profileId,
}: ProfileImagesProps) {
  const isTouch = useTouchDevice();
  const { track } = useAnalytics();
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [preloadIndex, setPreloadIndex] = useState<number | null>(null);

  const handleSelect = useCallback(
    (emblaApi: CarouselApi) => {
      if (!emblaApi) return;
      const index = emblaApi.selectedScrollSnap();
      setActiveIndex(index);
      const image = images[index];
      track('photo_viewed', {
        profile_id: profileId,
        metadata: {
          photo_index: index,
          photo_id: image?.id ?? null,
          total_photos: images.length,
        },
      });
    },
    [images, profileId, track],
  );

  useEffect(() => {
    if (!api) return;
    api.on('select', handleSelect);
    return () => {
      api.off('select', handleSelect);
    };
  }, [api, handleSelect]);

  if (!images.length) return <div className={cn(aspectClass, 'bg-muted')} />;

  return (
    // pointer-events-none on the outer wrapper is intentional — arrow buttons
    // and carousel interactions re-enable pointer-events via their own classes.
    // This prevents drag-selection of the image area.
    <div
      className={cn('relative overflow-hidden', aspectClass)}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      onTouchStart={() => {
        const next = (activeIndex + 1) % images.length;
        setPreloadIndex(next);
      }}
    >
      <Carousel opts={{ align: 'start', loop: true }} setApi={setApi}>
        <CarouselContent className="ml-0 h-full">
          {images.map((image, index) => (
            <CarouselItem key={image.id ?? index} className="pl-0">
              <PhotoSlide
                image={image}
                index={index}
                aspectClass={aspectClass}
                priority={index === 0 && priority}
                active={index === 0 || index === activeIndex || index === preloadIndex}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {images.length > 1 && !isTouch && (
          <ArrowWrapper>
            <CarouselPrevious className="absolute top-1/2 left-2 z-10 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100" />
          </ArrowWrapper>
        )}

        {images.length > 1 && !isTouch && (
          <ArrowWrapper>
            <CarouselNext className="absolute top-1/2 right-2 z-10 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100" />
          </ArrowWrapper>
        )}

        {/* Dot indicators — sliding pill */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2">
            <div className="relative flex items-center gap-1">
              {images.map((_, i) => (
                <span
                  key={i}
                  className="block h-1.5 w-1.5 rounded-full bg-white/40"
                />
              ))}
              {/* The pill slides to the active dot: each step = 6px dot + 4px gap = 10px */}
              <span
                className="pointer-events-none absolute top-0 left-0 block h-1.5 w-1.5 rounded-full bg-white transition-transform duration-300 ease-out"
                style={{ transform: `translateX(${activeIndex * 10}px)` }}
              />
            </div>
          </div>
        )}
      </Carousel>
    </div>
  );
}
